const AUTH_CODE_TO_KEY = {
  INVALID_CREDENTIALS: ['auth.login.error.invalidCredentials', 'The ID or password is incorrect.'],
  ID_PASSWORD_REQUIRED: ['auth.login.error.inputRequired', 'Please enter your ID and password.'],
  INVALID_LOGIN_INPUT: ['auth.login.error.invalidInput', 'The login input is invalid.'],
  ALREADY_LOGGED_IN: ['auth.login.error.alreadyLoggedIn', 'This account is already signed in.'],
  ACCOUNT_LOCKED: ['auth.login.error.accountLocked', 'This account is restricted by an administrator.'],
  LOGIN_SESSION_CHECK_FAILED: ['auth.login.error.sessionCheckFailed', 'Could not verify login status. Please try again shortly.'],
  LOGIN_IN_PROGRESS: ['auth.login.error.inProgress', 'A login is already being processed. Please try again shortly.'],
  SESSION_STORE_UNAVAILABLE: ['auth.login.error.sessionCheckFailed', 'Could not verify login status. Please try again shortly.'],
  SSO_NOT_CONFIGURED: ['auth.login.error.ssoNotConfigured', 'Automatic sign-in is not configured.'],
  SSO_SERVICE_UNAVAILABLE: ['auth.login.error.ssoServiceUnavailable', 'The linked sign-in service is unavailable. Please try again later.'],
  SSO_EXPIRED_OR_USED: ['auth.login.error.ssoExpiredOrUsed', 'This sign-in link has expired or was used. Please start again from Night House.'],
  ACCOUNT_UNAVAILABLE: ['auth.login.error.accountUnavailable', 'This account is unavailable.'],
  INVALID_SSO_CODE: ['auth.login.error.invalidSsoCode', 'This sign-in link is invalid. Please start again from Night House.'],
  TOKEN_MISSING: ['auth.login.error.tokenMissing', 'The login response did not include a token.'],
  SERVER_ERROR: ['auth.login.error.server', 'A server error occurred.'],
};

// Legacy backends may still return Korean sentence text instead of a stable code.
// These strings are INPUT MATCHERS ONLY and are never returned to the UI.
const MESSAGE_TO_CODE = new Map([
  ['아이디와 비밀번호를 입력해 주세요.', 'ID_PASSWORD_REQUIRED'],
  ['아이디 또는 비밀번호가 올바르지 않습니다.', 'INVALID_CREDENTIALS'],
  ['이미 접속 중입니다.', 'ALREADY_LOGGED_IN'],
  ['관리자에 의해 계정 이용이 제한되었습니다.', 'ACCOUNT_LOCKED'],
  ['로그인 상태 확인 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.', 'LOGIN_SESSION_CHECK_FAILED'],
  ['로그인 처리 중입니다. 잠시 후 다시 시도해 주세요.', 'LOGIN_IN_PROGRESS'],
  ['서버 오류가 발생했습니다.', 'SERVER_ERROR'],
]);

function responseData(source = {}) {
  return source?.response?.data || source?.payload || source?.data || source || {};
}

function translateKey(t, key, vars = null, fallback = '') {
  const k = String(key || '').trim();
  if (!k || typeof t !== 'function') return String(fallback || '');
  try {
    const value = t(k, vars, fallback);
    const text = value == null ? '' : String(value).trim();
    return text && text !== k ? text : String(fallback || '');
  } catch (_) {
    return String(fallback || '');
  }
}

export function getResponseCode(source = {}) {
  const data = responseData(source);
  return String(
    data?.code ||
    data?.error_code ||
    data?.errorCode ||
    data?.error ||
    source?.code ||
    ''
  ).trim().toUpperCase();
}

/**
 * Resolve API/socket errors without ever echoing a backend sentence directly.
 *
 * The server may return `message_key`/`messageKey`; those keys are safe to translate.
 * A stable code can be mapped by each caller via codeMap. Unknown server text is ignored
 * and the caller's localized fallback is shown instead. This prevents Korean (or any
 * other backend language) from leaking into a different UI language.
 */
export function resolveLocalizedResponseMessage(
  t,
  source = {},
  {
    vars = null,
    codeMap = null,
    fallbackKey = 'common.failed',
    fallback = 'The request could not be completed.',
  } = {},
) {
  const data = responseData(source);
  const messageKey = String(data?.message_key || data?.messageKey || '').trim();
  if (messageKey) {
    const translated = translateKey(t, messageKey, data?.message_vars || data?.messageVars || vars, '');
    if (translated) return translated;
  }

  const code = getResponseCode(source);
  const mapped = code && codeMap && codeMap[code];
  if (mapped) {
    if (Array.isArray(mapped)) {
      const [key, mappedFallback, mappedVars] = mapped;
      const translated = translateKey(t, key, mappedVars ?? vars, mappedFallback || fallback);
      if (translated) return translated;
    } else if (typeof mapped === 'string') {
      const translated = translateKey(t, mapped, vars, fallback);
      if (translated) return translated;
    } else if (mapped && typeof mapped === 'object') {
      const translated = translateKey(t, mapped.key, mapped.vars ?? vars, mapped.fallback || fallback);
      if (translated) return translated;
    }
  }

  return translateKey(t, fallbackKey, vars, fallback) || String(fallback || '');
}

export function resolveAuthErrorMessage(t, source = {}, fallback = '') {
  const data = responseData(source);
  const messageKey = String(data?.message_key || data?.messageKey || '').trim();
  if (messageKey) {
    const translated = translateKey(t, messageKey, data?.message_vars || data?.messageVars || null, '');
    if (translated) return translated;
  }

  let code = String(data?.error || data?.code || '').trim().toUpperCase();
  const rawMessage = String(data?.message || source?.message || '').trim();
  if (!code && rawMessage) code = MESSAGE_TO_CODE.get(rawMessage) || '';
  if (code === 'INVALID_CREDENTIALS' || (!code && rawMessage === '아이디 또는 비밀번호가 올바르지 않습니다.')) {
    return t('auth.login.error.invalidCredentials', null, 'The ID or password is incorrect.');
  }
  const mapped = AUTH_CODE_TO_KEY[code];
  if (mapped) return t(mapped[0], null, mapped[1]);
  if (!source?.response && (data?.isAxiosError || /^(ERR_NETWORK|ECONNABORTED|ETIMEDOUT|NETWORK_ERROR)$/.test(code))) {
    return t('auth.login.error.network', null, 'Could not reach the server. Check your connection and try again.');
  }
  return fallback || t('auth.login.error.failed', null, 'Login failed.');
}

export function translatedResponseMessage(t, response, successKey, successFallback, failureKey, failureFallback) {
  return response?.ok
    ? t(successKey, null, successFallback)
    : t(failureKey, null, failureFallback);
}


// Registration replies contain stable codes; never render server-supplied raw text
// because the API message is Korean and may include backend diagnostics.
const SIGNUP_CODE_TO_KEY = {
  AGENT_REFERRAL_TOKEN_REQUIRED: ['auth.signup.error.agentReferralTokenRequired', 'Please verify your agent code again.'],
  AGENT_CODE_MISMATCH: ['auth.signup.error.agentCodeMismatch', 'The agent code does not match the signup link.'],
  INVALID_AGENT_REFERRAL_TOKEN: ['auth.signup.error.invalidAgentReferralToken', 'This agent signup link is invalid or expired. Request a new link.'],
  INVALID_ID_LENGTH: ['auth.signup.error.invalidIdLength', 'Your ID must be 3 to 64 characters long.'],
  NICKNAME_REQUIRED: ['auth.signup.error.nicknameRequired', 'Please enter a nickname.'],
  INVALID_NICKNAME_LENGTH: ['auth.signup.error.invalidNicknameLength', 'Your nickname cannot exceed 64 characters.'],
  INVALID_PASSWORD_LENGTH: ['auth.signup.error.invalidPasswordLength', 'Your password must be 4 to 128 characters long.'],
  PASSWORD_MISMATCH: ['auth.signup.error.passwordMismatch', 'The passwords do not match.'],
  INVALID_RECOVERY_EMAIL: ['auth.signup.error.invalidRecoveryEmail', 'Please enter a valid recovery email address.'],
  INVALID_SECOND_PASSWORD_LENGTH: ['auth.signup.error.invalidSecondPasswordLength', 'Your secondary password must be 4 to 128 characters long.'],
  ID_ALREADY_EXISTS: ['auth.signup.error.idAlreadyExists', 'This ID is already in use.'],
  RECOVERY_EMAIL_ALREADY_EXISTS: ['auth.signup.error.recoveryEmailAlreadyExists', 'This recovery email is already registered to another account.'],
  INVALID_AGENT_CODE: ['auth.signup.error.invalidAgentCode', 'This agent code is invalid or inactive.'],
  AGENT_MODEL_NOT_READY: ['auth.signup.error.serviceUnavailable', 'Registration is temporarily unavailable. Please try again later.'],
  SUPERADMIN_NODE_NOT_FOUND: ['auth.signup.error.serviceUnavailable', 'Registration is temporarily unavailable. Please try again later.'],
  SUPERADMIN_ACCOUNT_UNAVAILABLE: ['auth.signup.error.serviceUnavailable', 'Registration is temporarily unavailable. Please try again later.'],
  ACCOUNT_ALREADY_EXISTS: ['auth.signup.error.accountConflict', 'A data conflict occurred while creating the account. Please retry shortly.'],
  SERVER_ERROR: ['auth.signup.error.server', 'An error occurred during registration. Please try again.'],
  OPERATIONS_RUNTIME_UNAVAILABLE: ['auth.signup.error.serviceUnavailable', 'Registration is temporarily unavailable. Please try again later.'],
  OPERATIONS_GUARD_FAILED: ['auth.signup.error.serviceUnavailable', 'Registration is temporarily unavailable. Please try again later.'],
  OPERATIONS_BLOCKED: ['auth.signup.error.serviceUnavailable', 'Registration is temporarily unavailable. Please try again later.'],
};

export function resolveSignupErrorMessage(t, source = {}) {
  const data = responseData(source);
  const messageKey = String(data?.message_key || data?.messageKey || '').trim();
  // Only known, packaged signup keys can be selected by the server.
  if (messageKey.startsWith('auth.signup.error.')) {
    const translated = translateKey(t, messageKey, null, '');
    if (translated) return translated;
  }
  const code = source?.message === 'INVALID_AGENT_CODE' ? 'INVALID_AGENT_CODE' : getResponseCode(source);
  const mapped = SIGNUP_CODE_TO_KEY[code];
  if (mapped) return translateKey(t, mapped[0], null, mapped[1]);
  if (source?.response?.status === 503 || source?.response?.status === 403) {
    return translateKey(t, 'auth.signup.error.serviceUnavailable', null, 'Registration is temporarily unavailable. Please try again later.');
  }
  if (source?.response?.status === 500) {
    return translateKey(t, 'auth.signup.error.server', null, 'An error occurred during registration. Please try again.');
  }
  if (!source?.response && (data?.isAxiosError || /^(ERR_NETWORK|ECONNABORTED|ETIMEDOUT|NETWORK_ERROR)$/.test(code))) {
    return translateKey(t, 'auth.signup.error.network', null, 'Could not reach the server. Check your connection and try again.');
  }
  return translateKey(t, 'auth.signup.error.failed', null, 'Account creation failed.');
}

// Redacted frontend diagnostic: Axios errors can contain request.config.data,
// which includes plaintext passwords; never log the original error object.
export function logAuthFailure(flow, source = {}) {
  const data = responseData(source);
  const reason = (source?.message === 'INVALID_AGENT_CODE' ? 'INVALID_AGENT_CODE' : getResponseCode(source)) || (source?.response?.status ? `HTTP_${source.response.status}` : 'NETWORK_OR_CLIENT_ERROR');
  console.warn('[AUTH][CLIENT_FAILURE]', {
    flow,
    reason,
    status: source?.response?.status || data?.status || null,
  });
}
